import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, finalize, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CrowdWalletService } from './crowd-wallet.service';
import { Router } from '@angular/router';
import {
  ChannelType,
  IAgents,
  IAmatesReport,
  IConversation,
  IMessage,
  IMessageHistory,
  MessageButtonType,
  MessageRole
} from '../models';

import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HttpClient } from '@angular/common/http';
import { report } from 'stacktrace-js';

export const HistoryURL = [
  environment.AMATES_AI_BASEURL,
  'get_chat_history/{0}'
].join('/');
export const ClearChatURL = [
  environment.AMATES_AI_BASEURL,
  'create_conversation'
].join('/');

interface ConversationResponse {
  created_at: string;
  id: number;
  state: string;
  title: string | null;
  username: string;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket;
  private messagesSubject: BehaviorSubject<IMessage[]> = new BehaviorSubject<
    IMessage[]
  >([]);
  public messages$ = this.messagesSubject.asObservable();

  private amatesReportSubject: BehaviorSubject<IAmatesReport> =
    new BehaviorSubject<IAmatesReport>({
      train: {
        progress: 0,
        report: [],
        finalized: false
      },
      build: {
        progress: 0,
        report: [],
        finalized: false
      }
    });
  public amatesReport$ = this.amatesReportSubject.asObservable();

  private static userMessageTyped = '';
  public loading = true;
  public channelType = new BehaviorSubject<ChannelType>(ChannelType.BUILD);
  public inputPassword = new BehaviorSubject<boolean>(false);

  private suggestedRepliesSubject = new BehaviorSubject<string[]>([]);
  public suggestedReplies$ = this.suggestedRepliesSubject.asObservable();
  private suggestedTitlesSubject = new BehaviorSubject<string[]>([]);
  public suggestedTitles$ = this.suggestedTitlesSubject.asObservable();
  private _newChatSubject = new Subject<boolean>();
  private _sendMessageSubject = new Subject<boolean>();
  private _selectAgentsSubject = new Subject<any>();
  private conversationIdSource = new BehaviorSubject<number | null>(null);
  conversationId$ = this.conversationIdSource.asObservable();

  constructor(
    private crowdWalletService: CrowdWalletService,
    private nzNotificationService: NzNotificationService,
    private router: Router,
    private http: HttpClient
  ) {
    if (environment.disableAi) {
      this.socket = {} as any;
      return;
    }

    this.socket = io(environment.AMATES_AI_BASEURL);

    const userEmail = this.crowdWalletService.userDetail.email;
    if (!userEmail) {
      this.router.navigate(['/', 'login-register']);
      return;
    }

    this.emitEvent('join', { room: userEmail, type: ChannelType.BUILD });

    this.listenMessages();
  }

  set userMessageTyped(value: string) {
    SocketService.userMessageTyped = value;
  }

  get userMessageTyped(): string {
    return SocketService.userMessageTyped;
  }

  get roomId(): string {
    return this.crowdWalletService.userDetail.email!;
  }

  // Method to emit events to the server
  emitEvent(eventName: string, data: any) {
    this.socket.emit(eventName, data);
  }

  // Method to listen to events from the server
  listenToEvent(eventName: string): Observable<any> {
    return new Observable((subscriber) => {
      this.socket.on(eventName, (data) => {
        subscriber.next(data);
      });

      // Cleanup on unsubscribe
      return () => {
        this.socket.off(eventName);
      };
    });
  }

  sendMessage(message: string, channelType: ChannelType) {
    this.loading = true;
    this.socket.send({
      message,
      room: this.roomId,
      type: channelType,
      jwt_token: this.crowdWalletService.userDetail.access_token,
      conversation_id: this.crowdWalletService.userDetail.conversation_id
    });
    this.refreshConversations();
  }

  public refreshConversations() {
    this._sendMessageSubject.next(true);
  }

  public selectAgents(conversation?: any) {
    this._selectAgentsSubject.next(conversation ? conversation : false);
  }

  // Optionally, add methods to disconnect/reconnect or handle other specific Socket.IO features
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  listenMessages() {
    this.listenToEvent('message').subscribe((resp: IMessage) => {
      if (typeof resp === 'string') {
        resp = {
          message: resp,
          role: MessageRole.system,
          type: ChannelType.ALL
        };
      }

      const message: IMessage[] = [];

      switch (resp.role) {
        case MessageRole.amates: {
          // if (resp.flag && resp.flag == 'Loading') {
          //   resp.message = '';
          // }
          this.handleSuggestedReplies(resp.suggested_replies ?? []);

          if (resp.report?.build) {
            resp.report.build.progress =
              (resp.report.build.progress ?? 0) >= 25
                ? resp.report.build.progress
                : 20;
            this.amatesProgressChanged(resp);
          }
          if (resp.report?.train) {
            resp.report.train.progress =
              (resp.report.train.progress ?? 0) >= 25
                ? resp.report.train.progress
                : 20;
            this.amatesProgressChanged(resp);
          }

          const switchtabRegex = new RegExp(/{switchtab}/, 'i');
          resp.hasButton = switchtabRegex.test(resp.message);
          resp.buttonType = MessageButtonType.switchtab;
          resp.messageList = resp.message.split('{switchtab}');

          const inputPasswordRegex = new RegExp(/password/, 'i');
          this.inputTypeChanged(inputPasswordRegex.test(resp.message));

          message.push(resp);
          break;
        }

        case MessageRole.system: {
          console.log(resp, 'report');
          if (resp.message.includes('Someone has joined')) {
            if (resp.report?.build || resp.report?.train) {
              resp.report.build.progress =
                (resp.report.build.progress ?? 0) >= 25
                  ? resp.report.build.progress
                  : 20;

              resp.report.train.progress =
                (resp.report.train.progress ?? 0) >= 25
                  ? resp.report.train.progress
                  : 20;

              const buildMessage: IMessage = {
                ...resp,
                report: resp.report
              };
              message.push(buildMessage);
              this.amatesProgressChanged(buildMessage);
            }
          } else if (resp.message.includes('Successfully')) {
            this.nzNotificationService.success('Success', resp.message, {
              nzPlacement: 'bottomRight'
            });
            message.push(resp);
          } else {
            this.nzNotificationService.error('Error', resp.message, {
              nzPlacement: 'bottomRight'
            });
            message.push(resp);
          }

          break;
        }
      }

      this.loading = false;
      if (
        !resp.conversation_id ||
        this.crowdWalletService.userDetail.conversation_id ===
          resp.conversation_id.toString()
      ) {
        this.addMessage(message);
      }
    });
  }

  addMessage(newMessage: IMessage[]) {
    const formattedMessages = newMessage.map((m) => ({
      ...m,
      message: m.message.replace(/\n/g, '<br />')
    }));

    try {
      // Replace single quotes with double quotes and parse JSON
      const jsonString = formattedMessages[0].message.replace(/'/g, '"');
      formattedMessages[0].message = JSON.parse(jsonString);
    } catch (error) {
      // If parsing fails, keep the original message
      console.log('JSON parsing failed:', error);
    }

    const currentMessages = this.messagesSubject.value;

    if (formattedMessages[0].flag) {
      const lastMessageIndex = currentMessages.length - 1;

      if (
        lastMessageIndex >= 0 &&
        currentMessages[lastMessageIndex].flag &&
        currentMessages[lastMessageIndex].flag === formattedMessages[0].flag
      ) {
        // Replace the last message with the new message
        currentMessages[lastMessageIndex] = formattedMessages[0];
        this.messagesSubject.next([...currentMessages]);
        return;
      }
    }

    this.messagesSubject.next([...currentMessages, ...formattedMessages]);
  }

  getAllMessage(): IMessage[] {
    return this.messagesSubject.value;
  }

  amatesProgressChanged(resp: IMessage) {
    const currentAmatesReport = this.amatesReportSubject.value;
    if (!resp.report) {
      return;
    }

    switch (resp.type) {
      case ChannelType.TRAIN:
        this.amatesReportSubject.next({
          ...currentAmatesReport,
          train: resp.report.train
        });
        break;

      case ChannelType.BUILD:
      default:
        this.amatesReportSubject.next({
          ...currentAmatesReport,
          build: resp.report.build
        });
        break;
    }
  }

  channelTypeChanged(data: ChannelType) {
    this.channelType.next(data);
  }

  inputTypeChanged(data: boolean) {
    this.inputPassword.next(data);
  }

  async loadMessagesHistory(conversationId: string) {
    try {
      const result = <IMessageHistory[]>(
        await this.getSelectedConversation(conversationId)
        // await this.http.get(HistoryURL.replace('{0}', this.room)).toPromise()
      );

      if (result.length > 0) {
        this.suggestedRepliesSubject.next([]);
        this.suggestedTitlesSubject.next([]);
      } else {
        this.handleSuggestedReplies([
          'I want responsive trading assistant',
          'I want to create a social media content creator',
          'I want to build a telegram price notifier',
          'I want to build a rsi calculator'
        ]);
        this.handleSuggestedTitles([
          'Social media content creator',
          'Telegram price notifier',
          'RSI calculator assistant',
          'Responsive trading assistant'
        ]);
      }

      const history: IMessage[] = result.map((m) => {
        let parsedMessage = m.message;

        try {
          // Replace single quotes with double quotes and parse JSON
          const jsonString = m.message.replace(/'/g, '"');
          parsedMessage = JSON.parse(jsonString);
        } catch (error) {
          console.log('JSON parsing failed:', error);
        }

        const message: IMessage = {
          message:
            typeof parsedMessage === 'string'
              ? parsedMessage.replace(/\n/g, '<br />')
              : parsedMessage,
          role: m.role === 'bot' ? MessageRole.amates : m.role,
          type: ChannelType.BUILD,
          report: undefined,
          flag: m.flag
        };

        if (
          message.role === MessageRole.amates &&
          typeof message.message === 'string'
        ) {
          const regex = new RegExp(/{switchtab}/, 'i');
          message.hasButton = regex.test(message.message);
          message.buttonType = MessageButtonType.switchtab;
          message.messageList = message.message.split('{switchtab}');
        }

        return message;
      });

      this.crowdWalletService.userDetail = {
        ...this.crowdWalletService.userDetail,
        conversation_id: conversationId
      };
      

      this.messagesSubject.next([...history]);
    } catch (err: any) {
      return [];
    }
  }

  async getSelectedConversation(
    item: string
  ): Promise<IMessageHistory[] | undefined> {
    try {
      const url = [
        environment.AMATES_AI_BASEURL,
        'get_conversation_history',
        this.crowdWalletService.userDetail.email,
        item
      ].join('/');
      return this.http
        .get<IMessageHistory[]>(url)
        .toPromise()
        .then((result) => result || []);
    } catch (e) {
      console.log(e);
    }
  }

  async clearChat() {
    try {
      this.loading = true;
      const trainUrl = [
        ClearChatURL,
        this.crowdWalletService.userDetail.email,
        ChannelType.TRAIN
      ].join('/');
      const buildUrl = [
        ClearChatURL,
        this.crowdWalletService.userDetail.email,
        ChannelType.BUILD
      ].join('/');

      const trainResult = await this.http
        .post<ConversationResponse>(trainUrl, {})
        .toPromise();
      const buildResult = await this.http
        .post<ConversationResponse>(buildUrl, {})
        .toPromise();

      const conversationId = trainResult?.id ?? buildResult?.id;
      
      this.crowdWalletService.userDetail = {
        ...this.crowdWalletService.userDetail,
        conversation_id: conversationId?.toString()
      };
      
      this.loading = false;

      if (trainResult && buildResult) {
        this.handleSuggestedReplies([
          'I want responsive trading assistant',
          'I want to create a social media content creator',
          'I want to build a telegram price notifier',
          'I want to build a rsi calculator'
        ]);
        this.handleSuggestedTitles([
          'Social media content creator',
          'Telegram price notifier',
          'RSI calculator assistant',
          'Responsive trading assistant'
        ]);

        const message = [
          {
            message: 'New Chat Started',
            role: MessageRole.system,
            type: ChannelType.TRAIN,
            report: {
              build: {
                progress: 20,
                report: [],
                finalized: false
              },
              train: {
                progress: 20,
                report: [],
                finalized: false
              }
            }
          },
          {
            message: 'New Chat Started',
            role: MessageRole.system,
            type: ChannelType.BUILD,
            report: {
              build: {
                progress: 20,
                report: [],
                finalized: false
              },
              train: {
                progress: 20,
                report: [],
                finalized: false
              }
            }
          }
        ];
        this.messagesSubject.next([...this.messagesSubject.value, ...message]);
        this.amatesProgressChanged(message[0]);
        this.amatesProgressChanged(message[1]);
      }
    } catch (error) {
      this.loading = false;
    }
  }

  handleSuggestedReplies(suggestedReplies: string[]) {
    this.suggestedRepliesSubject.next(suggestedReplies);
  }

  handleSuggestedTitles(suggestedTitles: string[]) {
    this.suggestedTitlesSubject.next(suggestedTitles);
  }

  get newChatSubject() {
    return this._newChatSubject;
  }

  get sendMessageSubject() {
    return this._sendMessageSubject;
  }

  get selectAgentsSubject() {
    return this._selectAgentsSubject;
  }

  async setNewChat(condition: boolean) {
    this.messagesSubject.next([]);
    this._newChatSubject.next(condition);
  }

  async deleteChat(item: IConversation) {
    try {
      const url = [
        environment.AMATES_AI_BASEURL,
        'delete_conversation',
        this.crowdWalletService.userDetail.email,
        item.id
      ].join('/');
      return this.http
        .delete<IMessageHistory[]>(url)
        .toPromise()
        .then((result) => result || []);
    } catch (e) {
      console.log(e);
    }
  }

  updateConversationId(newId: number) {
    this.conversationIdSource.next(newId);
  }

  public async importAgents(agents: IAgents[], item?: any) {
    if (item) {
      await this.importAgentsToConversation(agents, item.id);
      return;
    }
    try {
      const agentIds = agents.map((agent) => agent.id);
      const url = [
        environment.AMATES_AI_BASEURL,
        'create_conversation_with_agent'
      ].join('/');

      const data = {
        room_id: this.crowdWalletService.userDetail.email,
        room_type: ChannelType.BUILD,
        agent_ids: agentIds
      };

      const response = await this.http.post<any>(url, data).toPromise();
      this.crowdWalletService.userDetail = {
        ...this.crowdWalletService.userDetail,  
        conversation_id: response.id
      }

      this.refreshConversations();
      console.log(response);
    } catch (e) {
      console.log(e);
    }
  }

  public async importAgentsToConversation(agents: IAgents[], item: any) {
    try {
      const agentIds = agents.map((agent) => agent.id);
      const url = [
        environment.AMATES_AI_BASEURL,
        'add_agents_to_conversation'
      ].join('/');

      const data = {
        username: this.crowdWalletService.userDetail.email,
        conversation_id: item,
        room_type: ChannelType.BUILD,
        agent_ids: agentIds
      };

      const response = await this.http.post(url, data).toPromise();
      this.refreshConversations();
      console.log(response);
    } catch (e) {
      console.log(e);
    }
  }

  async getAllAgents(): Promise<IAgents[]> {
    try {
      const url = `${environment.AMATES_AI_BASEURL}/get_amates_agents`;
      const response = await this.http.get<IAgents[]>(url).toPromise();
      return response ?? []; // Ensures a non-undefined return value
    } catch (err: any) {
      console.error(`Error message: ${err.message}`);
      return [];
    }
  }
}
